/**
 * Playtest jump reachability (`q-o071-playtest-fixes` (d), owner playtest O-071) — the pure
 * half of the Playtest panel's World tab.
 *
 * The server hosts ONE zone per process (`GC_START_ZONE`, PLAYTEST.md "Which zone boots";
 * the local default is the Measured Yard, AD-648) and restores a character's checkpoint only
 * when it names that zone. A jump re-logs in as a seeded character, so a jump to a POI in any
 * other zone landed in the hosted zone's spawn — "every location loads the Measured Yard".
 *
 * `q-playtest-cross-zone-jump` (docs/features/playtest-panel.md §8): when the dev server routes
 * the POI's zone to its own backend (`npm run playtest:zones`), the jump first points the client
 * at that backend. Without a route the panel still names the restart instead of a button that
 * silently goes nowhere.
 */

import { zoneServerUrl } from '../../net/devZones.js';

export interface PoiZone {
  zone: string;
}

export type PoiReach =
  | { kind: 'jump' }
  | { kind: 'route'; zone: string }
  | { kind: 'restart'; command: string }
  | { kind: 'unknown' };

/**
 * Can this POI be reached? `hosted` is `helloAck.zoneId` (null pre-join); `routed` is the zone
 * list the dev server proxies (`routedDevZones()`). A route wins over the hosted zone so a jump
 * always lands on the backend that owns the POI's zone.
 */
export function poiReach(
  poi: PoiZone,
  hosted: string | null,
  routed: readonly string[] = [],
): PoiReach {
  if (routed.includes(poi.zone)) return { kind: 'route', zone: poi.zone };
  if (hosted === null) return { kind: 'unknown' };
  if (poi.zone === hosted) return { kind: 'jump' };
  return { kind: 'restart', command: `GC_START_ZONE=${poi.zone}` };
}

/** Where the chosen zone route is kept, so a reload stays on the same backend. */
export const ZONE_ROUTE_KEY = 'pxl.dev.zoneRoute';

interface RouteHost {
  __GC_CONFIG__?: { serverUrl?: string; assetUrl?: string; renderer?: string };
}

/**
 * Point the client at `zone`'s backend (null = back to the default server) by setting the
 * runtime server URL `net/platform.ts` reads per request. Persisted in `storage`.
 */
export function applyZoneRoute(
  zone: string | null,
  origin: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
  host: RouteHost = globalThis as RouteHost,
): void {
  const config = { ...(host.__GC_CONFIG__ ?? {}) };
  if (zone === null) delete config.serverUrl;
  else config.serverUrl = zoneServerUrl(origin, zone);
  host.__GC_CONFIG__ = config;
  try {
    if (zone === null) storage?.removeItem(ZONE_ROUTE_KEY);
    else storage?.setItem(ZONE_ROUTE_KEY, zone);
  } catch {
    /* storage unavailable — the route just will not survive a reload */
  }
}

/** Re-apply a stored route still served by this dev server; drops a stale one. Returns it. */
export function restoreZoneRoute(
  routed: readonly string[],
  origin: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
  host: RouteHost = globalThis as RouteHost,
): string | null {
  let zone: string | null = null;
  try {
    zone = storage?.getItem(ZONE_ROUTE_KEY) ?? null;
  } catch {
    zone = null;
  }
  if (zone === null) return null;
  if (!routed.includes(zone)) {
    applyZoneRoute(null, origin, storage, host);
    return null;
  }
  applyZoneRoute(zone, origin, storage, host);
  return zone;
}
